/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from '@material-ui/core';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { viewPortHeight_355 } from '../../../../SharedModules/CommonConstants/commonConstants';
import TableGridWithFeatures from '../../../../SharedModules/Table/TableGridWithFeatures';
import { checkAlloactionTableHead } from './TableHead';
import CustomSeparator from '../../../../SharedModules/Breadcrumbs/BreadcrumbsComponent';
import Spinner from '../../../../SharedModules/Spinner/Spinner';
import { convertStringDate, quarterToDate } from '../Utility';
import { handleScrollToEvent } from '../../../../SharedModules/CommonUtilities/commonUtilities';

const CheckAllocationComparisionDisplay = () => {
  const dispatch = useDispatch();
  const {
    displayObj: {
      checkPayValue,
      displayOptionValue,
      fromDateValue,
      toDateValue,
      checkAllocationComparisionData,
      checkPayeeCode,
    },
    isLoading,
  } = useSelector(
    (state) => state?.sharedState?.CheckAllocationComparisionReducer
  );

  const rebateDropdownData = useSelector(
    (state) =>
      state?.sharedState?.CheckAllocationComparisionReducer?.rebateDropdownData
        ?.data
  );

  const selectedCheckPayee =
    checkPayeeCode.find(
      (item) => item.codeValueID.codeValue === checkPayValue
    ) || {};
  const checkPayeeFullName = selectedCheckPayee
    ? `${selectedCheckPayee?.codeValueID?.codeValue}-${selectedCheckPayee?.shortDescription}`
    : '';

  const [rows, setRows] = React.useState([]);
  const [fullScreenView, setFullScreenView] = React.useState(false);
  const [filteredRows, setFilteredRow] = React.useState([]);
  const changeFullScreenView = () => {
    setFullScreenView(!fullScreenView);
  };

  React.useEffect(() => {
    if (checkAllocationComparisionData.length && rebateDropdownData.length) {
      // Create a map for rebate program codes and their long descriptions
      const rebateMap = rebateDropdownData.reduce((map, item) => {
        map[item.rebateProgramCd] = item.shortDescr;
        return map;
      }, {});

      // Process the data
      const processedData = checkAllocationComparisionData
        .sort((a, b) =>
          a.check_log_num > b.check_log_num
            ? 1
            : b.check_log_num > a.check_log_num
            ? -1
            : 0
        )
        .map((item) => ({
          ...item,
          check_log_num: item.check_log_num
            ? parseFloat(item.check_log_num.toFixed(2))
            : '0.00',
          credit_voucher_total: item.credit_voucher_total
            ? parseFloat(item.credit_voucher_total.toFixed(2))
            : '0.00',
          interest_voucher_total: item.interest_voucher_total
            ? parseFloat(item.interest_voucher_total.toFixed(2))
            : '0.00',
          unallocated: item.unallocated
            ? parseFloat(item.unallocated.toFixed(2))
            : '0.00',
          allocated: item.allocated
            ? parseFloat(item.allocated.toFixed(2))
            : '0.00',
          check_table_total: item.check_table_total
            ? parseFloat(item.check_table_total.toFixed(2))
            : '0.00',
          diff: item.diff ? parseFloat(item.diff.toFixed(2)) : '0.00',
          rebate_program_cd:
            rebateMap[item.rebate_program_cd] || item.rebate_program_cd,
        }));

      // Update rows with processed data
      setRows(processedData);
    }
  }, [checkAllocationComparisionData, rebateDropdownData]);

  const [filterApply, setFilterApply] = React.useState(false);

  const getFilterApply = (filterApplied) => {
    setFilterApply(filterApplied);
  };
  React.useEffect(() => {
    handleScrollToEvent();
  }, []);
  const onFilterChanged = (rowData, type) => {
    const updatedRowData = rowData.map((item) => {
      return {
        ...item,
      };
    });
    setFilteredRow(updatedRowData);
  };
  /* Below threee functions are for calculating data of table bottom pinned row */
  const sum = (property) => {
    const displayedRows = filterApply
      ? filteredRows
      : checkAllocationComparisionData;

    const data = displayedRows?.reduce((accumulator, object) => {
      return (
        accumulator + (object[property] ? parseFloat(object[property]) : null)
      );
    }, 0);
    return data ? parseFloat(data.toFixed(2)) : '0.00';
  };
  const createData = (count, prefix) => {
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push({
        isBottomRow: true,
        credit_voucher_total: sum('credit_voucher_total'),
        interest_voucher_total: sum('interest_voucher_total'),
        unallocated: sum('unallocated'),
        allocated: sum('allocated'),
        diff: sum('diff'),
        check_table_total: sum('check_table_total'),
        rebate_program_cd: 'Total:',
      });
    }
    return result;
  };
  const pinnedBottomRowData = useMemo(() => {
    return createData(1, 'Bottom');
  }, [checkAllocationComparisionData, filteredRows, filterApply]);
  const dateToShow = `${convertStringDate(quarterToDate(fromDateValue))} - ${
    toDateValue
      ? convertStringDate(quarterToDate(toDateValue, 'toDate'))
      : convertStringDate(quarterToDate(fromDateValue))
  }`;
  // Below 4 variable for PDF header slected information
  const firstInfo = '';
  const secondInfo = `Date Range: ${dateToShow}`;
  const thirdInfo = `${checkPayeeFullName}`;
  const fourthInfo = '';
  return (
    <div className="tabs-container">
      <Spinner load={isLoading} />
      <CustomSeparator
        parent1="Detail Reports"
        parent2="Display Check/Allocation Comparison"
      />
      <div className="tab-body">
        {!fullScreenView && (
          <>
            <div className="row">
              <div className="col-12  ">
                <div className="form-wrapper p-0 mt-2">
                  <div className="mui-custom-form input-md field-xl">
                    <div className="row mb-2">
                      <div className="col-4">
                        <TextField
                          label="Check Payee: "
                          id="Check Payee"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={`${checkPayeeFullName}`}
                        />
                      </div>
                      <div className="col-4">
                        <TextField
                          label="Date Range: "
                          id="Date Range"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={dateToShow}
                        />
                      </div>
                      <div className="col-4">
                        <TextField
                          label="Display Option: "
                          id="Display Option"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={displayOptionValue}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="row" style={{ marginTop: 15 }}>
          <div
            className="unallocated bottom-pinned-bold"
            style={{ width: '100vw' }}
          >
            <TableGridWithFeatures
              gridHeight={
                displayOptionValue === 'Totals' ? 100 : viewPortHeight_355
              }
              columnDefs={checkAlloactionTableHead}
              rowData={displayOptionValue === 'Totals' ? [] : rows}
              overlayNoRowsTemplate={
                displayOptionValue === 'Totals' ? '.' : undefined
              }
              data-testid="onEditingStopped"
              enterMovesDown
              showResetFilter={false}
              defaultCol={{
                wrapHeaderText: true,
                autoHeaderHeight: true,
              }}
              exportOnlyTotal={displayOptionValue}
              hideTotalCount={displayOptionValue === 'Totals'}
              exportFile
              fileName="CheckAllocationComparison"
              reportName="Check Allocation Comparision Report"
              fullScreenView={fullScreenView}
              handleFullScreenViewClick={changeFullScreenView}
              info={{ firstInfo, secondInfo, thirdInfo, fourthInfo }}
              pinnedBottomRowData={pinnedBottomRowData}
              onFilterData={onFilterChanged}
              getFilterApply={getFilterApply}
              isScrollOn
              frameworkComponents={{
                TextBoxRender: (cellInfo) => {
                  const name = cellInfo?.value;

                  if (name && name.length > 20) {
                    return (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                      >
                        <div>{name}</div>
                      </OverlayTrigger>
                    );
                  }
                  return <div>{name}</div>;
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default CheckAllocationComparisionDisplay;
